/**
 * Lazy Hover Video - Load videos only when needed (on hover or when visible)
 * This significantly improves page load performance by preventing all product card videos
 * from loading simultaneously.
 */
console.log('%c[Lazy Video] Script file loaded!', 'background: purple; color: white; font-size: 14px; padding: 4px;');

(function() {
    'use strict';

    // Store for loaded videos to prevent duplicate loading
    const loadedVideos = new WeakSet();
    
    // IntersectionObserver for visibility-based loading
    let videoObserver = null;

    /**
     * Initialize a video element inside the wrapper
     */
    function initializeVideo(wrapper) {
        if (loadedVideos.has(wrapper)) return wrapper.querySelector('video');
        
        const videoSrc = wrapper.dataset.videoSrc;
        const videoType = wrapper.dataset.videoType || 'video/mp4';

        if (!videoSrc) {
            console.warn('Lazy hover video: No video source found');
            return null;
        }

        console.log('Lazy hover video: Initializing video', videoSrc);

        // Create video element
        const video = document.createElement('video');
        video.muted = true;
        video.setAttribute('muted', ''); // For Safari
        video.loop = true;
        video.playsInline = true;
        video.preload = 'auto';

        // Create source element
        const source = document.createElement('source');
        source.src = videoSrc;
        source.type = videoType;
        video.appendChild(source);

        // Handle successful load
        video.addEventListener('loadeddata', function() {
            console.log('Lazy hover video: Video loaded', videoSrc);
            wrapper.classList.add('video-ready');
        }, { once: true });

        // Handle errors gracefully
        video.addEventListener('error', function(e) {
            console.error('Lazy hover video: Error loading', videoSrc, e);
            wrapper.classList.add('video-error');
        }, { once: true });

        // Append to wrapper (after the fallback image)
        wrapper.appendChild(video);
        loadedVideos.add(wrapper);

        // Trigger load
        video.load();

        return video;
    }

    /**
     * Play video on hover - called with the wrapper element
     */
    function handleHoverStart(wrapper) {
        let video = wrapper.querySelector('video');

        // Initialize video if not already done
        if (!video) {
            video = initializeVideo(wrapper);
        }

        if (!video) return;

        if (video.readyState >= 2) { // HAVE_CURRENT_DATA or better
            video.play().catch(function() {
                // Autoplay blocked - silent fail
            });
        } else {
            // Wait for video to be ready then play
            const playWhenReady = function() {
                video.play().catch(function() {});
            };
            video.addEventListener('canplay', playWhenReady, { once: true });
        }
    }

    /**
     * Pause video when hover ends
     */
    function handleHoverEnd(wrapper) {
        const video = wrapper.querySelector('video');
        
        if (video && !video.paused) {
            video.pause();
        }
    }

    /**
     * Initialize all lazy hover videos
     */
    function initLazyHoverVideos() {
        const lazyVideos = document.querySelectorAll('[data-video-lazy]:not(.lazy-video-initialized)');
        
        console.log('%c[Lazy Video] Found ' + lazyVideos.length + ' video wrappers', 'color: blue; font-weight: bold');
        
        lazyVideos.forEach(function(wrapper, index) {
            wrapper.classList.add('lazy-video-initialized');
            
            const videoSrc = wrapper.dataset.videoSrc;
            console.log('[Lazy Video] #' + index + ' video src:', videoSrc || 'NO SRC FOUND!');

            // Find the parent card element - this is what receives hover events
            const parentCard = wrapper.closest('.card-media') || wrapper.closest('.card') || wrapper.closest('.card-wrapper');
            
            if (!parentCard) {
                console.error('[Lazy Video] #' + index + ' No parent card found! Wrapper:', wrapper);
                return;
            }
            
            console.log('[Lazy Video] #' + index + ' Parent card found:', parentCard.className);

            // Add hover event listeners to the PARENT CARD (not the hidden wrapper)
            parentCard.addEventListener('mouseenter', function() {
                console.log('%c[Lazy Video] HOVER START on #' + index, 'color: green; font-weight: bold');
                handleHoverStart(wrapper);
            });
            
            parentCard.addEventListener('mouseleave', function() {
                console.log('[Lazy Video] Hover end on #' + index);
                handleHoverEnd(wrapper);
            });

            // For touch devices - watch for is-tapped class
            const observer = new MutationObserver(function(mutations) {
                mutations.forEach(function(mutation) {
                    if (mutation.attributeName === 'class') {
                        if (parentCard.classList.contains('is-tapped')) {
                            handleHoverStart(wrapper);
                        } else {
                            handleHoverEnd(wrapper);
                        }
                    }
                });
            });
            observer.observe(parentCard, { attributes: true, attributeFilter: ['class'] });

            // Use IntersectionObserver to preload video when nearly in viewport
            if (videoObserver) {
                videoObserver.observe(wrapper);
            }
        });
    }

    /**
     * Create IntersectionObserver for preloading videos near viewport
     */
    function createVideoObserver() {
        if (!('IntersectionObserver' in window)) return null;

        return new IntersectionObserver(function(entries) {
            entries.forEach(function(entry) {
                if (entry.isIntersecting) {
                    const wrapper = entry.target;
                    if (!loadedVideos.has(wrapper)) {
                        // Preload video when approaching viewport
                        initializeVideo(wrapper);
                    }
                    videoObserver.unobserve(wrapper);
                }
            });
        }, {
            rootMargin: '300px', // Start loading 300px before entering viewport
            threshold: 0
        });
    }

    /**
     * Initialize on DOM ready
     */
    function init() {
        console.log('Lazy hover video: Initializing...');
        videoObserver = createVideoObserver();
        initLazyHoverVideos();

        // Re-initialize when new content is added (e.g., infinite scroll, AJAX)
        if ('MutationObserver' in window) {
            const bodyObserver = new MutationObserver(function(mutations) {
                let shouldInit = false;
                mutations.forEach(function(mutation) {
                    if (mutation.addedNodes.length) {
                        mutation.addedNodes.forEach(function(node) {
                            if (node.nodeType === 1) {
                                if (node.matches && node.matches('[data-video-lazy]')) {
                                    shouldInit = true;
                                } else if (node.querySelector && node.querySelector('[data-video-lazy]')) {
                                    shouldInit = true;
                                }
                            }
                        });
                    }
                });
                if (shouldInit) {
                    initLazyHoverVideos();
                }
            });

            bodyObserver.observe(document.body, {
                childList: true,
                subtree: true
            });
        }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Also handle dynamic section rendering in Shopify theme editor
    document.addEventListener('shopify:section:load', initLazyHoverVideos);
})();
